import React, { useState, useEffect, useRef } from "react"
import { useHistory, useParams } from "react-router-dom"
import {
	Typography,
	Grid,
	Tab,
	Tabs,
	TextField,
	Button,
	Box,
	Tooltip
} from "@material-ui/core"

import {
	Edit as EditIcon,
	Info as InfoIcon,
	Delete as DeleteIcon,
	CalendarToday as ScheduleActionIcon,
	Send as SendIcon,
	Close as CancelScheduleIcon
} from "@material-ui/icons"

import {
	Divider,
	Loading,
	Notification,
	InfoDialog,
	PopConfirm,
	ActionDialog
} from "@/components"
import MessageBlastGroupPermissionGate from "@/components/PermissionGate/MessageBlastGroupPermissionGate"
import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"

import useDidMount from "@/hooks/useDidMount"
import useStyles from "@/pages/Admin/MessageBlast/Management/styles"

import WhatsappConnectionFlow from "@/@integrations/Whatsapp/components/WhatsappConnectionFlow"

import ErrorHandler from "@/services/ErrorHandler"
import ApiService from "@/services/Api"

import MessageBlastManagementSkeleton from "@/skeletons/Admin/MessageBlastManagement"
import {
	MessageBlastContact,
	MessageBlastContactToAdd,
	MessageBlastData,
	MessageBlastMessageType
} from "@/protocols/messageBlast"
import Contacts, { ContactsWhereData, CONTACTS_DEFAULT_WHERE_DATA } from "@/pages/Admin/MessageBlast/Management/Contacts"
import Messages, { MessageBlastContent } from "@/pages/Admin/MessageBlast/Management/Messages"
import FireMessagesDialog from "@/pages/Admin/MessageBlast/Management/FireMessagesDialog"
import Report, {
	ClientMessageBlastWhereData,
	CLIENT_MESSAGE_BLAST_DEFAULT_WHERE_DATA,
	PaginatedClientMessageBlast
} from "@/pages/Admin/MessageBlast/Management/Report"

import { getErrorName } from "@/utils/response"
import { fullDatetime } from "@/utils/time"
import Breadcrumb from "@/components/BreadcrumbNew"
import { renderMessageBlastStatusToMaterialChips } from "@/utils/messageBlast"
import MoreOptionsMenu from "@/components/MoreOptionsMenu"
import ScheduleDialog from "@/components/ScheduleDialog"
import MessageItem from "@/components/ChatMessageBuilder/MessageItem"
import { ErrorType } from "@/hooks/useValidation"
import { useMessageBlastUserInfoChip } from "@/hooks/useMessageBlastUserInfoChip "
import WABAChannelMessageTemplateService from "@/@integrations/WABA/services/WABAChannelMessageTemplate"
import { WABAChannelMessageTemplate } from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"
import { useGlobalStateStore } from "@/store/GlobalState"
import useWABACredits from "@/@integrations/WABA/hooks/useWABACredits"
import HardCoded from "@/services/HardCoded"

type ManagementTabType = "contacts" | "messages" | "whatsapp" | "report"

type ManagementTab = {
	title: string
	type: ManagementTabType
}

const MANAGEMENT_TABS: ManagementTab[] = [
	{
		title: "CONTATOS",
		type: "contacts"
	},
	{
		title: "MENSAGEM",
		type: "messages"
	},
	{
		title: "WHATSAPP",
		type: "whatsapp"
	},
	{
		title: "RELATÓRIO",
		type: "report"
	}
]

const Management = () => {
	const { checkPermission } = usePermissionStateStore()
	const [messageBlast, setMessageBlast] = useState({} as MessageBlastData)
	const [contacts, setContacts] = useState({} as MessageBlastContact[])
	const [templates, setTemplates] = useState<WABAChannelMessageTemplate[]>([])
	const [contactCount, setContactCount] = useState(0)
	const [contactsWhereData, setContactsWhereData] = useState<ContactsWhereData>(CONTACTS_DEFAULT_WHERE_DATA)
	const [openFireMessagesDialog, setOpenFireMessagesDialog] = useState(false)

	const [clientMessageBlasts, setClientMessageBlasts] = useState<PaginatedClientMessageBlast>({} as PaginatedClientMessageBlast)

	const [loading, setLoading] = useState(true)
	const [loadingContacts, setLoadingContacts] = useState(false)
	const [currentManagementTabType, setCurrentManagementTabType] = useState<ManagementTabType>(MANAGEMENT_TABS[0]?.type)
	const messageBlastId = +useParams<{ messageBlastId: string }>().messageBlastId
	const [openInfoDialog, setOpenInfoDialog] = useState(false)
	const [editDialogProps, setEditDialogProps] = useState({
		open: false,
		loading: false,
		messageBlast: {
			title: ""
		}
	})

	const [openScheduleMessagesDialog, setOpenScheduleMessagesDialog] = useState<boolean>(false)

	const classes = useStyles()

	const history = useHistory()

	const isMessageBlastSent = messageBlast.status === "SENT"

	const { hasEnoughCreditsToSendTemplate, creditsInfo } = useWABACredits()
	const canUseWABATemplateCreditsLimit = HardCoded.checkFeatureFlag("canUseWABATemplateCreditsLimit")

	const { renderMessageBlastUserInfoChip } = useMessageBlastUserInfoChip({ createdByUser: messageBlast.createdByUser, executedByUser: messageBlast.executedByUser })

	const globalStateStore = useGlobalStateStore()
	const inboxChannelId = globalStateStore.currentChannel?.id
	const channelType = globalStateStore.instance.current_channel_type
	const isMessageTypeWABA = channelType === "waba"

	const handleChangeCurrentManagementTabType = (type: ManagementTabType): void => {
		setCurrentManagementTabType(type)
	}

	const loadClientMessageBlasts = async (whereData: Partial<ClientMessageBlastWhereData> = CLIENT_MESSAGE_BLAST_DEFAULT_WHERE_DATA) => {
		try {
			const response = await ApiService.get(`/message-blasts/${messageBlastId}/report`, {
				params: whereData
			})

			setClientMessageBlasts(response.data)
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)
		}
	}

	const loadMessageBlast = async () => {
		try {
			const response = await ApiService.get(`/message-blasts/${messageBlastId}`)

			const responseMessageBlast = response.data.messageBlast

			setMessageBlast(responseMessageBlast)

			setEditDialogProps({
				...editDialogProps,
				messageBlast: {
					title: responseMessageBlast.title
				}
			})
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)
		}
	}

	// eslint-disable-next-line
	const loadContacts = useRef(async () => { })
	loadContacts.current = async () => {
		setLoadingContacts(true)

		try {
			const response = await ApiService.get(`/message-blasts/${messageBlastId}/clients`, {
				params: contactsWhereData
			})
			setContacts(response.data.rows)
			setContactCount(response.data.count)
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)
		}

		setLoadingContacts(false)
	}

	const loadTemplates = async () => {
		if (isMessageTypeWABA) {
			try {
				const response = await WABAChannelMessageTemplateService.retrieveAllByInboxChannelId(
					inboxChannelId
				)

				setTemplates(response)
			} catch (error) {
				ErrorHandler.handle(error as ErrorType)
			}
		}
	}

	const onMessageBlastChange = async (updateMessageBlast: MessageBlastData): Promise<boolean> => {
		try {
			await ApiService.put(`/message-blasts/${updateMessageBlast.id}`, updateMessageBlast)
			Notification.success({ message: "Envio alterado com sucesso!" })
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)
			Notification.error({ message: "Não foi possível alterar o envio!" })
		}

		await loadMessageBlast()
		return true
	}

	const handleChangeMessageBlast = async (data: Partial<MessageBlastData>): Promise<boolean> => {
		const messageBlastData = {
			...messageBlast,
			...data
		}

		return await onMessageBlastChange(messageBlastData)
	}

	const handleSaveMessages = async ({ messages, wabaChannelMessageTemplateId }: MessageBlastContent): Promise<void> => {
		const messageTypeToContent: Record<MessageBlastMessageType, Partial<MessageBlastData>> = {
			whatsapp: { content: { messages } },
			"waba-channel-message-template": { wabaChannelMessageTemplateId }
		}

		const content = messageTypeToContent[messageBlast.messageType]
		await handleChangeMessageBlast(content)
	}

	const handleFireMessages = async (): Promise<void> => {
		try {
			await ApiService.post(`/message-blasts/${messageBlastId}/fire`)

			await loadClientMessageBlasts()

			Notification.success({ message: "Envio disparado com sucesso!" })
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)

			const errorName = getErrorName(error as ErrorType)

			if (errorName === "ExceededMaxClients") {
				Notification.error({ message: "Você ultrapassou o limite de contatos!" })
			} else if (errorName === "ActionNotAvailableForAdminUser") {
				Notification.error({ message: "Não foi possivel realizar o Envio em Massa, pois você está autenticado via Admin!" })
			} else {
				Notification.error({ message: "Não foi possível disparar o envio!" })
			}
		}

		await loadMessageBlast()
	}

	const handleAddContacts = async (contacts: MessageBlastContactToAdd[]): Promise<void> => {
		await ApiService.post(`/message-blasts/${messageBlastId}/clients`, {
			clients: contacts
		})

		await loadContacts.current()
	}

	const handleClearContacts = async (): Promise<void> => {
		try {
			await ApiService.delete(`/message-blasts/${messageBlastId}/clients/all`)
			Notification.success({ message: "Contatos removidos com sucesso!" })
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)
			Notification.error({ message: "Não foi possível remover os contatos!" })
		}

		await loadContacts.current()
	}

	const handleRemoveContact = async (contact: MessageBlastContact) => {
		try {
			await ApiService.delete(`/message-blasts/${messageBlastId}/clients`, {
				data: {
					clients: [
						contact
					]
				}
			})
			Notification.success({ message: "Contato removido com sucesso!" })
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)
			Notification.error({ message: "Não foi possível remover o contato!" })
		}

		await loadContacts.current()
	}

	const handleCancelSchedule = async (messageBlastId: number, scheduleId?: number) => {
		PopConfirm.open({
			title: "Cancelar Agendamento",
			description: "Tem certeza? Essa ação é irreversível.",
			confirmButtonText: "CONTINUAR",
			cancelButtonText: "VOLTAR",
			onConfirm: async () => {
				try {
					await ApiService.post(`/message-blasts/scheduled/${messageBlastId}/cancel`, {
						scheduleId
					})

					await loadMessageBlast()
				} catch (error) {
					ErrorHandler.handle(error as ErrorType)

					if (getErrorName(error as ErrorType) === "MessageBlastAlreadySent") {
						Notification.error({ message: "Este envio já foi disparado." })
					}

					Notification.error({ message: "Houve um erro ao tentar cancelar o agendamento." })
				}
			}
		})
	}

	const handleDeleteMessageBlast = async (messageBlastId: number) => {
		PopConfirm.open({
			title: "Excluir envio",
			description: "Tem certeza? Essa ação é irreversível.",
			confirmButtonText: "APAGAR",
			cancelButtonText: "CANCELAR",
			onConfirm: async () => {
				try {
					await ApiService.delete(`/message-blasts/${messageBlastId}`)
					history.push("/admin/automation/message-blast")
				} catch (error) {
					Notification.error({ message: "Houve um erro." })
					ErrorHandler.handle(error as ErrorType)
				}
			}
		})
	}

	const setup = async () => {
		await Promise.all([
			loadMessageBlast(),
			loadClientMessageBlasts(),
			loadContacts.current(),
			loadTemplates()
		])

		setLoading(false)
	}

	const isFireButtonDisabled = (action: "fire" | "schedule") => {
		const isSent = isMessageBlastSent
		const noContacts = contactCount < 1

		const messageTypeToCheckContent: Record<MessageBlastMessageType, () => boolean> = {
			whatsapp: () => (messageBlast.content?.messages?.length || 0) >= 1,
			"waba-channel-message-template": () => Boolean(messageBlast.wabaChannelMessageTemplateId)
		}
		const hasContent = messageTypeToCheckContent[messageBlast.messageType]()

		if (isSent || noContacts || !hasContent) {
			return true
		}

		if (canUseWABATemplateCreditsLimit && action === "fire") {
			const messageBlastMessageTypeToCheckIfCreditsAreEnough: Record<MessageBlastMessageType, () => boolean> = {
				whatsapp: () => true,
				"waba-channel-message-template": () => {
					if (!messageBlast?.wabaChannelMessageTemplateId) {
						return false
					}

					const template = templates.find(template => template.id === messageBlast.wabaChannelMessageTemplateId)

					if (!template) {
						return false
					}

					return hasEnoughCreditsToSendTemplate(template, contactCount)
				}
			}

			const hasEnoughCredits = messageBlastMessageTypeToCheckIfCreditsAreEnough[messageBlast.messageType]()

			return !hasEnoughCredits
		}

		return false
	}

	const getFireButtonTooltipTitle = () => {
		if (canUseWABATemplateCreditsLimit) {
			const template = templates.find(template => template.id === messageBlast?.wabaChannelMessageTemplateId)

			if (template) {
				if (!hasEnoughCreditsToSendTemplate(template, contactCount)) {
					return `Você não possui créditos suficientes para enviar este template. Você possui ${creditsInfo.availableCredits} créditos disponíveis.`
				}
			}
		}

		if (isMessageBlastSent) {
			return "Você já disparou o envio."
		}

		if (contactCount < 1) {
			return "Adicione contatos antes de disparar o envio."
		}

		const messagesCount = messageBlast.content?.messages?.length || 0
		if (messagesCount < 1) {
			return "Adicione mensagens antes de disparar o envio."
		}

		return ""
	}

	const getMessageCount = () => {
		const MAX_MESSAGES_PER_MESSAGE_BLAST_WABA = 1
		const messageTypeToMessageCount: Record<MessageBlastMessageType, number> = {
			whatsapp: messageBlast.content?.messages?.length || 0,
			"waba-channel-message-template": messageBlast.wabaChannelMessageTemplateId ? MAX_MESSAGES_PER_MESSAGE_BLAST_WABA : 0
		}
		return messageTypeToMessageCount[messageBlast.messageType]
	}

	const handleOpenScheduleMessagesDialog = () => {
		setOpenScheduleMessagesDialog(true)
	}

	const handleOpenFireMessagesDialog = () => {
		setOpenFireMessagesDialog(true)
	}

	const handleCloseFireMessagesDialog = () => {
		setOpenFireMessagesDialog(false)
	}

	useDidMount(() => {
		setup()
	})

	useEffect(() => {
		loadContacts.current()
	}, [contactsWhereData])

	return (

		<Loading
			loading={loading}
			customLoadingElement={<MessageBlastManagementSkeleton />}
		>
			<>
				<Grid
					container
					direction="column"
				>
					<Grid
						alignItems="flex-start"
						justify="space-between"
						container
					>
						<Grid item xs>
							<Typography
								variant="h4"
								color="textPrimary"
								className={classes.breadcrumb}
							>
								<Breadcrumb
									data={[
										{ name: "Envios em Massa", pathname: "/admin/automation/message-blast" },
										{ name: "Detalhes do Envio", pathname: "/admin/automation/message-blast/" + messageBlast.id }
									]}
								/>

								<Divider orientation="vertical" size={2} />

								<InfoDialog
									openDialog={openInfoDialog}
									onClose={() => setOpenInfoDialog(false)}
									title="Informações do Envio"
								>
									<Typography variant="body1">
										Data de criação:  {fullDatetime(new Date(messageBlast.createdAt))}
									</Typography>

									{messageBlast.status === "SENT" && (
										<Typography variant="body1">
											Data de envio: {fullDatetime(new Date(messageBlast.statusDate))}
										</Typography>
									)}
								</InfoDialog>

								<ActionDialog
									title="Alterar título do envio"
									openDialog={editDialogProps.open}
									fullWidth
									loading={editDialogProps.loading}
									onSave={async () => {
										setEditDialogProps({ ...editDialogProps, loading: true })
										await handleChangeMessageBlast(editDialogProps.messageBlast)
										setEditDialogProps({ ...editDialogProps, open: false, loading: false })
									}}
									onClose={() => setEditDialogProps({ ...editDialogProps, open: false })}
								>
									<TextField
										placeholder="Título"
										variant="outlined"
										color="primary"
										value={editDialogProps.messageBlast.title}
										onChange={async ({ target }) => setEditDialogProps({
											...editDialogProps,
											messageBlast: { title: target.value as string }
										})}
										fullWidth
									/>
								</ActionDialog>
							</Typography>
						</Grid>
					</Grid>

					<Divider orientation="horizontal" size={5} />

					<Grid
						container
						alignItems="flex-start"
						spacing={2}
					>
						<Grid item>
							<Box display="flex" alignItems="center">
								{renderMessageBlastStatusToMaterialChips(messageBlast)}
							</Box>
						</Grid>

						<Grid item>
							{renderMessageBlastUserInfoChip()}
						</Grid>
					</Grid>

					{
						messageBlast?.title?.length > 55 && <Divider orientation="horizontal" size={2} />
					}

					<Grid
						container
						alignItems="center"
						justifyContent="space-between"
						style={{ minHeight: "50px" }}
					>
						<Grid item xs={6}>
							<Typography variant="body1">
								<h3 className={classes.messageBlastTitle}>{messageBlast.title}</h3>
							</Typography>
						</Grid>
						<Grid item>
							<Grid container direction="row" spacing={2} justifyContent="center" alignItems="center">
								{
									messageBlast.status === "DRAFT" && <Grid item>
										<MessageBlastGroupPermissionGate action="schedule">
											<Button
												variant="outlined"
												className={isFireButtonDisabled("schedule") ? "" : classes.scheduleMesssageButton}
												onClick={handleOpenScheduleMessagesDialog}
												disabled={isFireButtonDisabled("schedule")}
												style={{ height: "50px" }}
											>
												<ScheduleActionIcon fontSize="medium" />
												<Divider orientation="vertical" size={1} />
												AGENDAR ENVIO
											</Button>
										</MessageBlastGroupPermissionGate>
									</Grid>
								}
								{
									messageBlast.status === "DRAFT" && <Grid item>
										<Tooltip
											title={
												getFireButtonTooltipTitle()
											}
										>
											<span>
												<MessageBlastGroupPermissionGate action="fire">
													<Button
														variant="contained"
														color="primary"
														onClick={handleOpenFireMessagesDialog}
														disabled={isFireButtonDisabled("fire")}
														style={{ height: "3.125rem" }}
													>
														<SendIcon fontSize="medium" />
														<Divider orientation="vertical" size={1} />
														ENVIAR AGORA
													</Button>
												</MessageBlastGroupPermissionGate>
											</span>
										</Tooltip>
									</Grid>
								}
								{
									messageBlast.status === "SCHEDULED" && <Grid item>
										<MessageBlastGroupPermissionGate action="cancel-scheduled">
											<Button
												variant="outlined"
												className={classes.cancelScheduleMesssageButton}
												onClick={() => handleCancelSchedule(messageBlast.id, messageBlast?.schedule?.id)}
												style={{ height: "3.125rem" }}
											>
												<CancelScheduleIcon fontSize="medium" />
												<Divider orientation="vertical" size={1} />
												CANCELAR AGENDAMENTO
											</Button>
										</MessageBlastGroupPermissionGate>
									</Grid>
								}

								<Grid item>
									<MoreOptionsMenu
										options={[
											{
												title: "Detalhes",
												icon: <InfoIcon />,
												showIcon: true,
												onClick: async () => setOpenInfoDialog(true)
											},
											{
												title: "Alterar",
												icon: <EditIcon />,
												showIcon: true,
												disabled: !checkPermission("message-blast", "update"),
												disabledTooltip: PERMISSION_DENIED_MESSAGE,
												onClick: async () => setEditDialogProps({ ...editDialogProps, open: true })
											},
											{
												title: "Excluir",
												icon: <DeleteIcon />,
												showIcon: true,
												color: "danger",
												disabled: !checkPermission("message-blast", "delete"),
												disabledTooltip: PERMISSION_DENIED_MESSAGE,
												onClick: async () => handleDeleteMessageBlast(messageBlast.id)
											}
										]}
										enableBackground={true}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Divider size={6} orientation="horizontal" />

					<Tabs
						color="primary"
						value={currentManagementTabType}
						className={classes.chatTabs}
						onChange={(_, value) => handleChangeCurrentManagementTabType(value)}
						classes={{
							indicator: classes.chatTabIndicator
						}}
					>
						{MANAGEMENT_TABS.map(managementTab => {
							const isReportTab = managementTab.type === "report"

							if (isReportTab && !isMessageBlastSent) {
								return null
							}

							return (
								<Tab
									key={managementTab.title}
									label={managementTab.title}
									value={managementTab.type}
									className={classes.chatTab}
								/>
							)
						})}
					</Tabs>

					<Divider size={3} orientation="horizontal" />

					{currentManagementTabType === "contacts" && (
						<Contacts
							messageBlastId={messageBlastId}
							isMessageBlastSent={messageBlast.status === "SENT"}
							contacts={contacts}
							contactCount={contactCount}
							onClearContacts={handleClearContacts}
							onRemoveContact={handleRemoveContact}
							onAddContacts={handleAddContacts}
							whereData={contactsWhereData}
							setWhereData={setContactsWhereData}
							loading={loadingContacts}
							loadContacts={loadContacts}
							messagesInMessageBlastCount={getMessageCount()}
						/>
					)}

					{currentManagementTabType === "messages" && (
						<Messages
							isMessageBlastSent={messageBlast.status === "SENT"}
							messageBlastMessageType={messageBlast.messageType}
							messages={messageBlast.content?.messages || []}
							templates={templates}
							wabaChannelMessageTemplateId={messageBlast?.wabaChannelMessageTemplateId}
							onSave={handleSaveMessages}
						/>
					)}

					{currentManagementTabType === "whatsapp" && (
						<WhatsappConnectionFlow />
					)}

					{currentManagementTabType === "report" && (
						<Report
							clientMessageBlasts={clientMessageBlasts}
							loadClientMessageBlasts={loadClientMessageBlasts}
						/>
					)}
				</Grid>

				<FireMessagesDialog
					open={openFireMessagesDialog}
					onClose={handleCloseFireMessagesDialog}
					onFire={handleFireMessages}
					contactCount={contactCount}
					messages={messageBlast?.content?.messages || []}
				/>

				<ScheduleDialog
					title="Confirme os dados para o envio"
					openDialog={openScheduleMessagesDialog}
					createScheduleText="AGENDAR ENVIO"
					contactCount={contactCount}
					messageCount={messageBlast?.content?.messages?.length ?? 0}
					onClose={() => setOpenScheduleMessagesDialog(false)}
					scheduleData={{
						callback: {
							type: "webhook"
						},
						payload: {
							messageBlastId
						},
						inboxChannelId: messageBlast?.inboxChannelId
					}}
					actionTrigger="message-blast"
					onSchedule={async () => {
						setOpenScheduleMessagesDialog(false)

						if (canUseWABATemplateCreditsLimit) {
							ActionDialog.open({
								openDialog: true,
								title: "ATENÇÃO",
								children: "Certifique-se de ter créditos de mensagem suficientes no momento do envio para garantir que o template seja enviado com sucesso.",
								saveText: "Estou ciente",
								onSave: () => {
									setOpenScheduleMessagesDialog(false)
								},
								hideCloseButton: true,
								forcedRender: true
							})
						}

						await loadMessageBlast()
					}}
					fullWidth
				>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<b>Número de contatos:</b> {contactCount}
						</Grid>

						<Divider orientation="horizontal" size={1} />

						<Grid item xs={12}>
							<b>Mensagens:</b>
						</Grid>

						<Grid item xs={12}>
							<Grid
								container
								alignItems="flex-start"
								justify="flex-start"
								spacing={1}
							>
								{messageBlast?.content?.messages?.map(message => (
									<Grid item xs={12} key={message.id}>
										<Grid container>
											<MessageItem
												type={message.type}
												content={message.content}
												mediaName={message.mediaName}
											/>
										</Grid>
									</Grid>
								))}
							</Grid>
						</Grid>
					</Grid>
				</ScheduleDialog>
			</>
		</Loading >
	)
}

export default Management
